import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarDays, ChevronDown, Percent } from "lucide-react";
import { PayoutAdjustments } from "./PayoutAdjustments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const parseDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);

const weekStartOf = (iso) => {
  const d = parseDate(iso);
  if (!d) return "";
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const weekLabel = (iso) => {
  const s = parseDate(iso);
  const e = parseDate(iso);
  e.setDate(e.getDate() + 6);
  return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]}`;
};
const dayLabel = (iso) => {
  const d = parseDate(iso);
  if (!d) return "—";
  return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const Stat = ({ label, value, tone, testid }) => (
  <div className="flex-1 text-center" data-testid={testid}>
    <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`text-sm font-black ${tone}`}>{money(value)}</p>
  </div>
);

export const ContractorBreakdown = () => {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState({});
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [grain, setGrain] = useState("week");
  const [open, setOpen] = useState({});

  const load = () => {
    axios
      .get(`${API}/contractor-breakdown`)
      .then(({ data }) => {
        setJobs(data.jobs || []);
        setStatus(data.payout_status || {});
        setAdjustments(data.adjustments || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const markPaid = async (weekKey, contractor, paid) => {
    try {
      await axios.put(`${API}/contractor-payout/status`, { date: weekKey, contractor, paid });
      toast.success(paid ? `${contractor} marked paid` : `${contractor} marked unpaid`);
      load();
    } catch {
      toast.error("Could not update payout status");
    }
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillPast = async () => {
    if (!window.confirm("Mark every past week (before this week) as paid, for both crew and contractors? You can still change any week individually.")) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(`${API}/payments/backfill-paid`);
      toast.success(`Marked ${data.contractors_marked} contractor week(s) & ${data.movers_marked} crew week(s) as paid.`);
      load();
    } catch {
      toast.error("Could not mark past weeks paid");
    } finally {
      setBackfilling(false);
    }
  };

  const adjIndex = useMemo(() => {
    const idx = {};
    (adjustments || []).forEach((a) => {
      const k = `${a.week_start}|${a.payee_name}`;
      (idx[k] = idx[k] || []).push(a);
    });
    return idx;
  }, [adjustments]);

  const groups = useMemo(() => {
    const applyAdj = grain === "week";
    const map = new Map();
    jobs.forEach((j) => {
      if (!j.date) return;
      const key = grain === "week" ? weekStartOf(j.date) : j.date;
      const payment = (Number(j.labour) || 0) + (Number(j.extras) || 0);
      const share = Number(j.share) || 0;
      const g = map.get(key) || { key, byContractor: new Map() };
      const c = g.byContractor.get(j.contractor) || { contractor: j.contractor, contractor_id: j.contractor_id, share_percent: j.share_percent, payment: 0, share: 0, jobs: [] };
      c.payment += payment;
      c.share += share;
      c.jobs.push({ ...j, payment: r2(payment), profit: r2(payment - share) });
      g.byContractor.set(j.contractor, c);
      map.set(key, g);
    });
    return Array.from(map.values())
      .map((g) => {
        const wk = weekStartOf(g.key);
        const contractors = Array.from(g.byContractor.values())
          .map((c) => {
            const st = status[`${wk}|${c.contractor}`] || {};
            const adj = applyAdj ? adjIndex[`${wk}|${c.contractor}`] || [] : [];
            const adjNet = r2(adj.reduce((s, a) => s + (Number(a.signed) || 0), 0));
            const payout = r2(c.share + adjNet);
            return { ...c, payment: r2(c.payment), share: r2(c.share), adj, adjNet, payout, profit: r2(c.payment - payout), paid: !!st.paid, paid_at: st.paid_at || "" };
          })
          .sort((a, b) => b.payout - a.payout);
        const payment = r2(contractors.reduce((s, c) => s + c.payment, 0));
        const share = r2(contractors.reduce((s, c) => s + c.share, 0));
        const payout = r2(contractors.reduce((s, c) => s + c.payout, 0));
        return { key: g.key, weekKey: wk, payment, share, payout, profit: r2(payment - payout), contractors };
      })
      .sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [jobs, grain, status, adjIndex]);

  const totals = useMemo(() => groups.reduce((a, g) => ({ payment: a.payment + g.payment, payout: a.payout + g.payout, profit: a.profit + g.profit }), { payment: 0, payout: 0, profit: 0 }), [groups]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="contractor-breakdown-loading">
        <Loader2 className="h-5 w-5 animate-spin text-blaze" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="contractor-breakdown">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-indigo-700">
            <CalendarDays className="h-4 w-4" /> Contractor share & profit
          </p>
          <p className="mt-0.5 text-sm text-slate-500">Job payment, contractor share and company profit, per {grain === "week" ? "week" : "day"}. Tap to see each job.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={backfillPast}
            disabled={backfilling}
            className="rounded-xl border border-green-600 bg-green-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-green-700 transition-colors hover:bg-green-600 hover:text-white disabled:opacity-50"
            data-testid="cbreak-backfill-btn"
          >
            {backfilling ? "Marking…" : "Mark past weeks paid"}
          </button>
          <div className="flex gap-1 rounded-xl border border-line bg-slate-100/70 p-1">
            {[["week", "Weekly"], ["day", "Daily"]].map(([g, label]) => (
              <button
                key={g}
                onClick={() => setGrain(g)}
                className={`rounded-lg px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${grain === g ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`cbreak-grain-${g}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="mt-4 rounded-xl border border-dashed border-line bg-slate-50/60 py-10 text-center text-sm text-slate-500" data-testid="contractor-breakdown-empty">
          No completed contractor jobs yet.
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-stretch gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="cbreak-grand-totals">
            <Stat label="Total payment" value={totals.payment} tone="text-slate-900" testid="cbreak-grand-payment" />
            <div className="w-px bg-slate-200" />
            <Stat label="Contractor payout" value={totals.payout} tone="text-indigo-700" testid="cbreak-grand-payout" />
            <div className="w-px bg-slate-200" />
            <Stat label="Company profit" value={totals.profit} tone="text-green-700" testid="cbreak-grand-profit" />
          </div>

          <div className="mt-3 space-y-2.5">
            {groups.map((g) => (
              <div key={g.key} className="rounded-2xl border border-indigo-200/70 bg-indigo-50/30" data-testid={`cbreak-period-${g.key}`}>
                <button
                  type="button"
                  onClick={() => setOpen((p) => ({ ...p, [g.key]: !p[g.key] }))}
                  className="flex w-full items-center justify-between gap-3 p-4 text-left"
                  data-testid={`cbreak-toggle-${g.key}`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-slate-900">{grain === "week" ? weekLabel(g.key) : dayLabel(g.key)}</span>
                    <span className="text-[11px] text-slate-500">{g.contractors.length} contractor{g.contractors.length === 1 ? "" : "s"} · profit {money(g.profit)}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-right">
                      <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400">Payout</span>
                      <span className="font-display text-xl font-semibold text-indigo-700" data-testid={`cbreak-total-${g.key}`}>{money(g.payout)}</span>
                    </span>
                    <ChevronDown className={`h-4 w-4 text-indigo-400 transition-transform ${open[g.key] ? "rotate-180" : ""}`} />
                  </span>
                </button>
                {open[g.key] && (
                  <div className="space-y-2 border-t border-indigo-200/60 p-3" data-testid={`cbreak-detail-${g.key}`}>
                    <div className="flex items-stretch gap-2 rounded-xl bg-white px-2 py-2 shadow-sm">
                      <Stat label="Payment" value={g.payment} tone="text-slate-900" testid={`cbreak-period-payment-${g.key}`} />
                      <div className="w-px bg-slate-200" />
                      <Stat label="Payout" value={g.payout} tone="text-indigo-700" testid={`cbreak-period-share-${g.key}`} />
                      <div className="w-px bg-slate-200" />
                      <Stat label="Profit" value={g.profit} tone="text-green-700" testid={`cbreak-period-profit-${g.key}`} />
                    </div>
                    {g.contractors.map((c) => (
                      <div key={c.contractor} className="rounded-xl bg-white p-3 shadow-sm" data-testid={`cbreak-contractor-${g.key}-${c.contractor}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5 text-sm font-bold text-slate-800">
                            <span className="truncate">{c.contractor}</span>
                            <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
                              <Percent className="h-2.5 w-2.5" />{c.share_percent}
                            </span>
                            <span
                              className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${c.paid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                              data-testid={`cbreak-status-${g.key}-${c.contractor}`}
                            >
                              {c.paid ? "Paid" : "Unpaid"}
                            </span>
                          </span>
                          <span className="shrink-0 text-right">
                            <span className="block text-[9px] font-bold uppercase tracking-wider text-slate-400">Payout</span>
                            <span className="font-black text-indigo-700" data-testid={`cbreak-payout-${g.key}-${c.contractor}`}>{money(c.payout)}</span>
                          </span>
                        </div>
                        <div className="mt-2 space-y-1.5">
                          {c.jobs.map((j) => (
                            <div key={j.job_id} className="rounded-lg bg-slate-50 px-2.5 py-2" data-testid={`cbreak-job-${j.job_id}`}>
                              <div className="flex items-center justify-between text-xs">
                                <span className="font-semibold text-slate-700">
                                  {j.job_id} <span className="font-normal text-slate-400">· {j.date}</span>
                                  <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${j.source === "movers_buddy" ? "bg-amber-100 text-amber-700" : "bg-blaze/10 text-blaze"}`}>
                                    {j.source === "movers_buddy" ? "Buddy" : "MN"}
                                  </span>
                                </span>
                              </div>
                              <div className="mt-1.5 flex items-stretch gap-2">
                                <Stat label="Payment" value={j.payment} tone="text-slate-900" testid={`cbreak-job-payment-${j.job_id}`} />
                                <div className="w-px bg-slate-200" />
                                <Stat label="Share" value={j.share} tone="text-indigo-700" testid={`cbreak-job-share-${j.job_id}`} />
                                <div className="w-px bg-slate-200" />
                                <Stat label="Profit" value={j.profit} tone="text-green-700" testid={`cbreak-job-profit-${j.job_id}`} />
                              </div>
                            </div>
                          ))}
                        </div>
                        {grain === "week" && (
                          <>
                            <PayoutAdjustments
                              payeeType="contractor"
                              payeeName={c.contractor}
                              contractorId={c.contractor_id}
                              weekStart={g.weekKey}
                              jobs={c.jobs.map((j) => ({ job_id: j.job_id, date: j.date }))}
                              items={c.adj}
                              onChange={load}
                              idPrefix={`cadj-${g.key}-${c.contractor}`}
                            />
                            {c.adjNet !== 0 && (
                              <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500" data-testid={`cbreak-netline-${g.key}-${c.contractor}`}>
                                <span>Base share {money(c.share)} {c.adjNet >= 0 ? "+" : "−"} {money(Math.abs(c.adjNet))}</span>
                                <span className="font-bold text-indigo-700">= {money(c.payout)}</span>
                              </div>
                            )}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => markPaid(g.weekKey, c.contractor, !c.paid)}
                          className={`mt-2.5 w-full rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors ${c.paid ? "bg-slate-500 hover:bg-slate-600" : "bg-green-600 hover:bg-green-700"}`}
                          data-testid={`cbreak-mark-${g.key}-${c.contractor}`}
                        >
                          {c.paid ? "Mark Unpaid" : "Mark Paid"}
                        </button>
                        {c.paid && c.paid_at ? <p className="mt-1 text-center text-[10px] text-slate-400">Paid {c.paid_at}</p> : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
